/**
 * EmailAutomationTab - Manage segment-based email campaigns and lifecycle triggers
 */
import { useState, useEffect, useCallback } from 'react';
import { 
  Mail, Send, Users, Zap, Play, Pause, Plus, Edit2, Trash2,
  Eye, RefreshCw, Settings, Crown, Heart, Star, Sparkles,
  TrendingUp, AlertCircle, Clock, Moon, UserX, AlertTriangle,
  CheckCircle, XCircle, BarChart3, ArrowRight, Copy, ExternalLink
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import DOMPurify from 'dompurify';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs';
import { api } from '@/utils/apiClient';

// Segment icons and colors
const SEGMENT_CONFIG = {
  champions: { icon: Crown, color: '#22c55e', name: 'Champions' },
  loyal_customers: { icon: Heart, color: '#3b82f6', name: 'Loyal Customers' },
  potential_loyalists: { icon: Star, color: '#8b5cf6', name: 'Potential Loyalists' },
  new_customers: { icon: Sparkles, color: '#06b6d4', name: 'New Customers' },
  promising: { icon: TrendingUp, color: '#f59e0b', name: 'Promising' },
  needs_attention: { icon: AlertCircle, color: '#eab308', name: 'Needs Attention' },
  about_to_sleep: { icon: Clock, color: '#f97316', name: 'About to Sleep' },
  at_risk: { icon: AlertTriangle, color: '#ef4444', name: 'At Risk' },
  hibernating: { icon: Moon, color: '#6b7280', name: 'Hibernating' },
  lost: { icon: UserX, color: '#374151', name: 'Lost' }
};

export default function EmailAutomationTab() {
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  
  // Data states
  const [stats, setStats] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [rules, setRules] = useState([]);
  const [sendHistory, setSendHistory] = useState([]);
  const [segmentTemplates, setSegmentTemplates] = useState({});
  
  // Modal states
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [showTriggerModal, setShowTriggerModal] = useState(false);
  const [showTestEmailModal, setShowTestEmailModal] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [selectedSegment, setSelectedSegment] = useState(null);
  const [previewHtml, setPreviewHtml] = useState('');
  const [testEmail, setTestEmail] = useState('');
  
  // Fetch all data
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [statsRes, templatesRes, rulesRes, historyRes, segmentRes] = await Promise.all([
        api.get('/admin/email-automation/stats', { params: { days: 30 } }),
        api.get('/admin/email-automation/templates'),
        api.get('/admin/email-automation/rules'),
        api.get('/admin/email-automation/send-history', { params: { limit: 20 } }),
        api.get('/admin/email-automation/segment-templates')
      ]);

      setStats(statsRes.data);
      setTemplates(templatesRes.data.templates || []);
      setRules(rulesRes.data.rules || []);
      setSendHistory(historyRes.data.history || []);
      setSegmentTemplates(segmentRes.data.segments || {});
    } catch (err) {
      console.error('Failed to fetch email automation data:', err);
      toast.error('Failed to load email automation data');
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Preview template
  const previewTemplate = async (templateId) => {
    try {
      const res = await api.get(`/admin/email-automation/preview/${templateId}`);
      setPreviewHtml(res.data.html);
      setShowPreviewModal(true);
    } catch (err) {
      toast.error('Failed to load preview');
    }
  };

  // Send test email
  const sendTestEmail = async () => {
    if (!testEmail || !selectedTemplate) return;
    
    try {
      const res = await api.post('/admin/email-automation/send-test', null, {
        params: { template_id: selectedTemplate.template_id, test_email: testEmail }
      });
      
      if (res.data.success) {
        toast.success(res.data.message);
        setShowTestEmailModal(false);
        setTestEmail('');
      } else {
        toast.error(res.data.message || 'Failed to send test email');
      }
    } catch (err) {
      toast.error('Failed to send test email');
    }
  };

  // Trigger segment campaign
  const triggerSegmentCampaign = async (segment, dryRun = true) => {
    try {
      const res = await api.post(`/admin/email-automation/trigger-segment/${segment}`, null, {
        params: { dry_run: dryRun }
      });
      
      if (dryRun) {
        setSelectedSegment({ ...res.data, segment });
        setShowTriggerModal(true);
      } else {
        toast.success(`Campaign sent! ${res.data.emails_sent} emails delivered.`);
        setShowTriggerModal(false);
        fetchData();
      }
    } catch (err) {
      toast.error('Failed to trigger campaign');
    }
  };

  // Toggle rule
  const toggleRule = async (ruleId) => {
    try {
      const res = await api.post(`/admin/email-automation/rules/${ruleId}/toggle`);
      setRules(rules.map(r => 
        r.rule_id === ruleId ? { ...r, active: res.data.active } : r
      ));
      toast.success(`Rule ${res.data.active ? 'activated' : 'paused'}`);
    } catch (err) {
      toast.error('Failed to toggle rule');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Never';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  };

  if (loading && !stats) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="email-automation-tab">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-heading text-foreground">Email Automation</h2>
          <p className="text-muted-foreground">Lifecycle email campaigns based on customer segments</p>
        </div>
        <Button onClick={fetchData} variant="outline" className="gap-2">
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Quick Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <StatCard
            label="Emails Sent"
            value={stats.summary?.total_emails_sent || 0}
            icon={Send}
            color="blue"
          />
          <StatCard
            label="Open Rate"
            value={`${stats.summary?.open_rate || 0}%`}
            icon={Mail}
            color="green"
          />
          <StatCard
            label="Click Rate"
            value={`${stats.summary?.click_rate || 0}%`}
            icon={Zap}
            color="purple"
          />
          <StatCard
            label="Active Rules"
            value={`${stats.summary?.active_rules || 0}/${stats.summary?.total_rules || 0}`}
            icon={Settings}
            color="orange"
          />
          <StatCard
            label="Discounts Redeemed"
            value={stats.discounts?.redeemed || 0}
            icon={CheckCircle}
            color="teal"
          />
        </div>
      )}

      {/* Main Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="grid w-full grid-cols-4 lg:w-auto lg:inline-grid">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="templates">Templates</TabsTrigger>
          <TabsTrigger value="campaigns">Campaigns</TabsTrigger>
          <TabsTrigger value="history">History</TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-6">
          {/* Segment Campaign Cards */}
          <div className="bg-card rounded-xl p-6 border border-border">
            <h3 className="font-medium text-foreground mb-4 flex items-center gap-2">
              <Users className="w-5 h-5 text-primary" />
              Segment-Based Campaigns
            </h3>
            <p className="text-sm text-muted-foreground mb-4">
              Trigger email campaigns for specific customer segments. Each segment has tailored messaging and discount offers.
            </p>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {Object.entries(SEGMENT_CONFIG).map(([segmentId, config]) => {
                const Icon = config.icon;
                const segmentData = segmentTemplates[segmentId];
                const discountConfig = segmentData?.discount_config;
                
                return (
                  <div 
                    key={segmentId}
                    className="bg-muted/30 rounded-lg p-4 border border-border hover:border-primary/50 transition-colors"
                  >
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <div 
                          className="w-8 h-8 rounded-lg flex items-center justify-center"
                          style={{ backgroundColor: `${config.color}20` }}
                        >
                          <Icon className="w-4 h-4" style={{ color: config.color }} />
                        </div>
                        <span className="font-medium text-foreground">{config.name}</span>
                      </div>
                      {discountConfig && (
                        <span className="text-xs px-2 py-1 rounded-full bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300">
                          {discountConfig.percent}% off
                        </span>
                      )}
                    </div>
                    
                    <div className="text-xs text-muted-foreground mb-3">
                      {segmentData?.templates?.length > 0 ? (
                        <span>{segmentData.templates.length} template(s) ready</span>
                      ) : (
                        <span>Default template</span>
                      )}
                    </div>
                    
                    <Button 
                      size="sm" 
                      className="w-full gap-2"
                      onClick={() => triggerSegmentCampaign(segmentId, true)}
                    >
                      <Send className="w-3 h-3" />
                      Send Campaign
                    </Button>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Performance by Segment */}
          {stats?.by_segment?.length > 0 && (
            <div className="bg-card rounded-xl p-6 border border-border">
              <h3 className="font-medium text-foreground mb-4 flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-primary" />
                Performance by Segment
              </h3>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="text-left py-3 px-2 font-medium text-muted-foreground">Segment</th>
                      <th className="text-right py-3 px-2 font-medium text-muted-foreground">Sent</th>
                      <th className="text-right py-3 px-2 font-medium text-muted-foreground">Opened</th>
                      <th className="text-right py-3 px-2 font-medium text-muted-foreground">Open Rate</th>
                      <th className="text-right py-3 px-2 font-medium text-muted-foreground">Clicked</th>
                      <th className="text-right py-3 px-2 font-medium text-muted-foreground">Click Rate</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stats.by_segment.map((seg) => {
                      const config = SEGMENT_CONFIG[seg.segment] || {};
                      const Icon = config.icon || Users;
                      return (
                        <tr key={seg.segment} className="border-b border-border/50">
                          <td className="py-3 px-2">
                            <div className="flex items-center gap-2">
                              <Icon className="w-4 h-4" style={{ color: config.color }} />
                              <span>{config.name || seg.segment}</span>
                            </div>
                          </td>
                          <td className="py-3 px-2 text-right">{seg.emails_sent}</td>
                          <td className="py-3 px-2 text-right">{seg.opened}</td>
                          <td className="py-3 px-2 text-right">
                            <span className={seg.open_rate > 20 ? 'text-green-600' : ''}>
                              {seg.open_rate}%
                            </span>
                          </td>
                          <td className="py-3 px-2 text-right">{seg.clicked}</td>
                          <td className="py-3 px-2 text-right">
                            <span className={seg.click_rate > 5 ? 'text-green-600' : ''}>
                              {seg.click_rate}%
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </TabsContent>

        {/* Templates Tab */}
        <TabsContent value="templates" className="space-y-4">
          <div className="flex justify-between items-center">
            <h3 className="font-medium text-foreground">Email Templates</h3>
            <Button onClick={() => setShowTemplateModal(true)} className="gap-2">
              <Plus className="w-4 h-4" />
              New Template
            </Button>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            {templates.map((template) => {
              const segmentConfig = SEGMENT_CONFIG[template.trigger_segment] || {};
              const Icon = segmentConfig.icon || Mail;
              
              return (
                <div 
                  key={template.template_id}
                  className="bg-card rounded-xl p-5 border border-border"
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div 
                        className="w-10 h-10 rounded-xl flex items-center justify-center"
                        style={{ backgroundColor: `${segmentConfig.color || '#666'}20` }}
                      >
                        <Icon className="w-5 h-5" style={{ color: segmentConfig.color || '#666' }} />
                      </div>
                      <div>
                        <h4 className="font-medium text-foreground">{template.name}</h4>
                        <p className="text-xs text-muted-foreground">
                          {segmentConfig.name || template.trigger_segment}
                        </p>
                      </div>
                    </div>
                    <span className={`text-xs px-2 py-1 rounded-full ${
                      template.active ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300' : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400'
                    }`}>
                      {template.active ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  
                  <p className="text-sm text-muted-foreground mb-3 line-clamp-2">
                    Subject: {template.subject}
                  </p>
                  
                  <div className="flex gap-2">
                    <Button 
                      size="sm" 
                      variant="outline"
                      onClick={() => previewTemplate(template.template_id)}
                      className="gap-1"
                    >
                      <Eye className="w-3 h-3" />
                      Preview
                    </Button>
                    <Button 
                      size="sm" 
                      variant="outline"
                      onClick={() => {
                        setSelectedTemplate(template);
                        setShowTestEmailModal(true);
                      }}
                      className="gap-1"
                    >
                      <Send className="w-3 h-3" />
                      Test
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        </TabsContent>

        {/* Campaigns Tab */}
        <TabsContent value="campaigns" className="space-y-4">
          <div className="flex justify-between items-center">
            <h3 className="font-medium text-foreground">Automation Rules</h3>
          </div>

          {rules.length === 0 ? (
            <div className="bg-card rounded-xl p-8 border border-border text-center">
              <Zap className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <h4 className="font-medium text-foreground mb-2">No automation rules yet</h4>
              <p className="text-sm text-muted-foreground mb-4">
                Create automation rules to automatically send emails when customers enter specific segments.
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {rules.map((rule) => (
                <div 
                  key={rule.rule_id}
                  className="bg-card rounded-xl p-4 border border-border flex items-center justify-between"
                >
                  <div className="flex items-center gap-4">
                    <Switch 
                      checked={rule.active} 
                      onCheckedChange={() => toggleRule(rule.rule_id)}
                    />
                    <div>
                      <h4 className="font-medium text-foreground">{rule.name}</h4>
                      <p className="text-sm text-muted-foreground">
                        Trigger: {rule.trigger_segment} • Template: {rule.template_id}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <span>{rule.emails_sent || 0} sent</span>
                    <Button size="sm" variant="ghost">
                      <Edit2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        {/* History Tab */}
        <TabsContent value="history" className="space-y-4">
          <h3 className="font-medium text-foreground">Recent Email Activity</h3>
          
          {sendHistory.length === 0 ? (
            <div className="bg-card rounded-xl p-8 border border-border text-center">
              <Mail className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <h4 className="font-medium text-foreground mb-2">No emails sent yet</h4>
              <p className="text-sm text-muted-foreground">
                Trigger a segment campaign to start sending automated emails.
              </p>
            </div>
          ) : (
            <div className="bg-card rounded-xl border border-border overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-muted/30 border-b border-border">
                    <th className="text-left py-3 px-4 font-medium text-muted-foreground">Recipient</th>
                    <th className="text-left py-3 px-4 font-medium text-muted-foreground">Template</th>
                    <th className="text-left py-3 px-4 font-medium text-muted-foreground">Segment</th>
                    <th className="text-center py-3 px-4 font-medium text-muted-foreground">Status</th>
                    <th className="text-right py-3 px-4 font-medium text-muted-foreground">Sent</th>
                  </tr>
                </thead>
                <tbody>
                  {sendHistory.map((item) => {
                    const segmentConfig = SEGMENT_CONFIG[item.segment] || {};
                    return (
                      <tr key={`history-${item.email}-${item.sent_at}`} className="border-b border-border/50 hover:bg-muted/20">
                        <td className="py-3 px-4">
                          <span className="text-foreground">{item.email}</span>
                        </td>
                        <td className="py-3 px-4 text-muted-foreground">{item.template_name}</td>
                        <td className="py-3 px-4">
                          <span style={{ color: segmentConfig.color }}>
                            {segmentConfig.name || item.segment}
                          </span>
                        </td>
                        <td className="py-3 px-4 text-center">
                          {item.status === 'sent' || item.status === 'delivered' ? (
                            <span className="inline-flex items-center gap-1 text-green-600">
                              <CheckCircle className="w-4 h-4" />
                              {item.opened && '• Opened'}
                              {item.clicked && '• Clicked'}
                            </span>
                          ) : item.status === 'simulated' ? (
                            <span className="text-blue-600">Simulated</span>
                          ) : (
                            <span className="inline-flex items-center gap-1 text-red-600">
                              <XCircle className="w-4 h-4" />
                              Failed
                            </span>
                          )}
                        </td>
                        <td className="py-3 px-4 text-right text-muted-foreground">
                          {formatDate(item.sent_at)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Preview Modal */}
      <Dialog open={showPreviewModal} onOpenChange={setShowPreviewModal}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Email Preview</DialogTitle>
          </DialogHeader>
          <div 
            className="border rounded-lg overflow-hidden"
            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(previewHtml) }}
          />
        </DialogContent>
      </Dialog>

      {/* Test Email Modal */}
      <Dialog open={showTestEmailModal} onOpenChange={setShowTestEmailModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Send Test Email</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Template</Label>
              <p className="text-sm text-muted-foreground mt-1">
                {selectedTemplate?.name}
              </p>
            </div>
            <div>
              <Label htmlFor="test-email">Email Address</Label>
              <Input 
                id="test-email"
                type="email"
                value={testEmail}
                onChange={(e) => setTestEmail(e.target.value)}
                placeholder="your@email.com"
                className="mt-1"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTestEmailModal(false)}>
              Cancel
            </Button>
            <Button onClick={sendTestEmail} disabled={!testEmail}>
              <Send className="w-4 h-4 mr-2" />
              Send Test
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Trigger Campaign Modal */}
      <Dialog open={showTriggerModal} onOpenChange={setShowTriggerModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm Campaign Send</DialogTitle>
          </DialogHeader>
          {selectedSegment && (
            <div className="space-y-4 py-4">
              <div className="bg-muted/30 rounded-lg p-4">
                <h4 className="font-medium text-foreground mb-2">Campaign Summary</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Segment:</span>
                    <span className="font-medium">{SEGMENT_CONFIG[selectedSegment.segment]?.name}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Recipients:</span>
                    <span className="font-medium">{selectedSegment.customers_found} customers</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Template:</span>
                    <span className="font-medium">{selectedSegment.template}</span>
                  </div>
                </div>
              </div>
              
              {selectedSegment.would_send_to?.length > 0 && (
                <div>
                  <Label className="text-sm">Preview Recipients (first 10):</Label>
                  <div className="mt-2 text-xs text-muted-foreground space-y-1">
                    {selectedSegment.would_send_to.map((email) => (
                      <div key={`preview-${email}`}>{email}</div>
                    ))}
                  </div>
                </div>
              )}
              
              <div className="bg-yellow-50 text-yellow-800 rounded-lg p-3 text-sm">
                <AlertTriangle className="w-4 h-4 inline mr-2" />
                This will send emails to all {selectedSegment.customers_found} customers in this segment.
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTriggerModal(false)}>
              Cancel
            </Button>
            <Button 
              onClick={() => triggerSegmentCampaign(selectedSegment?.segment, false)}
              className="gap-2"
            >
              <Send className="w-4 h-4" />
              Send Campaign
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Stat Card Component
function StatCard({ label, value, icon: Icon, color }) {
  const colorClasses = {
    blue: 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400',
    green: 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400',
    purple: 'bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400',
    orange: 'bg-orange-100 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400',
    teal: 'bg-teal-100 dark:bg-teal-900/30 text-teal-600 dark:text-teal-400'
  };

  return (
    <div className="bg-card rounded-xl p-4 border border-border">
      <div className="flex items-center justify-between mb-2">
        <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${colorClasses[color]}`}>
          <Icon className="w-4 h-4" />
        </div>
      </div>
      <p className="text-xl font-bold text-foreground">{value}</p>
      <p className="text-xs text-muted-foreground">{label}</p>
    </div>
  );
}
